<template>
  <div class="approval-timeline">
    <!-- 결재선 -->
    <div v-if="approvalSteps.length > 0" class="step-group">
      <h4 class="group-title">결재선</h4>
      <el-steps
        :active="activeApprovalIndex"
        finish-status="success"
        align-center
        style="margin-bottom: 15px;"
      >
        <el-step
          v-for="step in approvalSteps"
          :key="step.id"
          :title="step.step_name"
          :description="getStepDescription(step)"
          :status="getStepStatus(step)"
        />
      </el-steps>
      <el-table :data="approvalSteps" border size="small">
        <el-table-column prop="sequence" label="순서" width="60" align="center" />
        <el-table-column prop="step_name" label="단계명" width="120" />
        <el-table-column prop="role" label="역할" width="100" />
        <el-table-column label="결재자" width="120">
          <template #default="{ row }">{{ row.approver_name || '미지정' }}</template>
        </el-table-column>
        <el-table-column label="상태" width="80">
          <template #default="{ row }">
            <el-tag :type="getTagType(row.status)" size="small">
              {{ row.status_display || row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="처리일시" width="150">
          <template #default="{ row }">{{ formatDateTime(row.approved_at) }}</template>
        </el-table-column>
        <el-table-column prop="comments" label="코멘트" />
        <el-table-column v-if="showActions" label="" width="120">
          <template #default="{ row }">
            <el-button-group v-if="isMyPendingStep(row)" size="small">
              <el-button type="success" @click="handleAction(row, true)">승인</el-button>
              <el-button type="danger" @click="handleAction(row, false)">반려</el-button>
            </el-button-group>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 합의선 -->
    <div v-if="agreementSteps.length > 0" class="step-group">
      <h4 class="group-title">합의선</h4>
      <el-table :data="agreementSteps" border size="small">
        <el-table-column prop="sequence" label="순서" width="60" align="center" />
        <el-table-column prop="step_name" label="단계명" width="120" />
        <el-table-column prop="role" label="역할" width="100" />
        <el-table-column label="합의자" width="120">
          <template #default="{ row }">{{ row.approver_name || '미지정' }}</template>
        </el-table-column>
        <el-table-column label="상태" width="80">
          <template #default="{ row }">
            <el-tag :type="getTagType(row.status)" size="small">
              {{ row.status_display || row.status }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="처리일시" width="150">
          <template #default="{ row }">{{ formatDateTime(row.approved_at) }}</template>
        </el-table-column>
        <el-table-column prop="comments" label="코멘트" />
        <el-table-column v-if="showActions" label="" width="120">
          <template #default="{ row }">
            <el-button-group v-if="isMyPendingStep(row)" size="small">
              <el-button type="success" @click="handleAction(row, true)">동의</el-button>
              <el-button type="danger" @click="handleAction(row, false)">반려</el-button>
            </el-button-group>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 참조선 -->
    <div v-if="referenceSteps.length > 0" class="step-group">
      <h4 class="group-title">참조</h4>
      <el-table :data="referenceSteps" border size="small">
        <el-table-column label="참조자">
          <template #default="{ row }">{{ row.approver_name || '미지정' }}</template>
        </el-table-column>
        <el-table-column label="상태" width="80">
          <template #default="{ row }">
            <el-tag :type="getTagType(row.status)" size="small">
              {{ row.status_display || row.status }}
            </el-tag>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <el-empty v-if="allSteps.length === 0" description="결재 정보가 없습니다." />

    <!-- 승인/반려 코멘트 다이얼로그 -->
    <el-dialog v-model="showDialog" title="결재 처리" width="400px" append-to-body>
      <el-form :model="actionForm">
        <el-form-item label="코멘트">
          <el-input
            v-model="actionForm.comments"
            type="textarea"
            :rows="3"
            placeholder="코멘트 입력 (선택)"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="showDialog = false">취소</el-button>
        <el-button
          :type="actionForm.isApproved ? 'success' : 'danger'"
          @click="submitAction"
          :loading="submitting"
        >
          {{ actionForm.isApproved ? '승인' : '반려' }}
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from 'vue'
import { useUserStore } from '@/stores/user'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'

const props = defineProps({
  approvalRequest: { type: Object, default: null },
  legacySteps: { type: Array, default: () => [] },
  documentApiPath: { type: String, default: '' },
  documentId: { type: [String, Number], default: null },
  showActions: { type: Boolean, default: true },
})

const emit = defineEmits(['refresh', 'approved', 'rejected'])
const userStore = useUserStore()

const showDialog = ref(false)
const submitting = ref(false)
const actionForm = reactive({ step: null, isApproved: true, comments: '' })

// 전체 steps (통합 또는 레거시)
const allSteps = computed(() => {
  if (props.approvalRequest?.steps?.length > 0) {
    return props.approvalRequest.steps
  }
  return props.legacySteps
})

// step_type별 분리 (하위 호환: step_type 없으면 APPROVAL)
const approvalSteps = computed(() =>
  allSteps.value.filter(s => !s.step_type || s.step_type === 'APPROVAL')
)
const agreementSteps = computed(() =>
  allSteps.value.filter(s => s.step_type === 'AGREEMENT')
)
const referenceSteps = computed(() =>
  allSteps.value.filter(s => s.step_type === 'REFERENCE')
)

const isUnifiedApproval = computed(() => !!props.approvalRequest?.id)

const activeApprovalIndex = computed(() => {
  const idx = approvalSteps.value.findIndex(s => s.status === 'PENDING')
  return idx === -1 ? approvalSteps.value.length : idx
})

const isMyPendingStep = (step) => {
  if (!userStore.profile) return false
  if (step.status !== 'PENDING') return false
  if (step.approver !== userStore.profile.id) return false

  // APPROVAL: 현재 순서의 단계만 처리 가능
  if (!step.step_type || step.step_type === 'APPROVAL') {
    const currentApproval = approvalSteps.value.find(s => s.status === 'PENDING')
    return currentApproval && currentApproval.id === step.id
  }
  // AGREEMENT: 언제든 처리 가능
  if (step.step_type === 'AGREEMENT') return true

  return false
}

const getStepStatus = (step) => {
  const map = {
    APPROVED: 'success',
    REJECTED: 'error',
    PENDING: 'wait',
    SKIPPED: 'finish',
    INVALIDATED: 'error',
    ACKNOWLEDGED: 'success',
  }
  return map[step.status] || 'wait'
}

const getStepDescription = (step) => {
  if (step.status === 'APPROVED') return `${step.approver_name || ''} 승인`
  if (step.status === 'REJECTED') return `${step.approver_name || ''} 반려`
  if (step.status === 'PENDING') return step.approver_name || '대기'
  return step.status_display || step.status
}

const getTagType = (status) => {
  const map = {
    PENDING: 'info',
    APPROVED: 'success',
    REJECTED: 'danger',
    SKIPPED: 'warning',
    INVALIDATED: 'info',
    ACKNOWLEDGED: 'success',
  }
  return map[status] || 'info'
}

const formatDateTime = (dt) => {
  if (!dt) return '-'
  return new Date(dt).toLocaleString('ko-KR')
}

const handleAction = (step, isApproved) => {
  actionForm.step = step
  actionForm.isApproved = isApproved
  actionForm.comments = ''
  showDialog.value = true
}

const submitAction = async () => {
  submitting.value = true
  try {
    if (isUnifiedApproval.value) {
      const endpoint = actionForm.isApproved ? 'approve' : 'reject'
      await axiosInstance.post(`approvals/${props.approvalRequest.id}/${endpoint}/`, {
        is_approved: actionForm.isApproved,
        comments: actionForm.comments,
      })
    } else if (props.documentApiPath && props.documentId) {
      await axiosInstance.post(`${props.documentApiPath}/${props.documentId}/approve/`, {
        is_approved: actionForm.isApproved,
        comments: actionForm.comments,
      })
    }

    ElMessage.success(actionForm.isApproved ? '승인되었습니다.' : '반려되었습니다.')
    showDialog.value = false
    emit('refresh')
    emit(actionForm.isApproved ? 'approved' : 'rejected')
  } catch (error) {
    console.error('Approval action failed:', error)
    ElMessage.error(error.response?.data?.error || '처리 중 오류가 발생했습니다.')
  } finally {
    submitting.value = false
  }
}
</script>

<style scoped>
.step-group {
  margin-bottom: 20px;
}
.group-title {
  margin: 16px 0 10px;
  font-size: 14px;
  color: #303133;
}
.step-group:first-child .group-title {
  margin-top: 0;
}
</style>
